import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { subDays, format } from 'date-fns'
import type { DateRange } from '../types'

interface AnalyticsState {
  dateRange: DateRange
  timezone: string | null
  useSystemTimezone: boolean
  setDateRange: (range: DateRange) => void
  setPresetRange: (days: number) => void
  setTimezone: (timezone: string | null) => void
  setUseSystemTimezone: (use: boolean) => void
}

const getDefaultDateRange = (): DateRange => ({
  from: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
  to: format(new Date(), 'yyyy-MM-dd'),
})

export const useAnalyticsStore = create<AnalyticsState>()(
  persist(
    (set) => ({
      dateRange: getDefaultDateRange(),
      timezone: null,
      useSystemTimezone: true,

      setDateRange: (range) => set({ dateRange: range }),

      setPresetRange: (days) =>
        set({
          dateRange: {
            from: format(subDays(new Date(), days), 'yyyy-MM-dd'),
            to: format(new Date(), 'yyyy-MM-dd'),
          },
        }),

      setTimezone: (timezone) => set({ timezone }),

      setUseSystemTimezone: (use) =>
        set({
          useSystemTimezone: use,
          timezone: use ? null : Intl.DateTimeFormat().resolvedOptions().timeZone,
        }),
    }),
    {
      name: 'analytics-storage',
      partialize: (state) => ({
        timezone: state.timezone,
        useSystemTimezone: state.useSystemTimezone,
      }),
    }
  )
)

// Helper to get formatted date range for API
export const getFormattedDateRange = () => {
  const { dateRange } = useAnalyticsStore.getState()
  return dateRange
}

// Helper to get timezone for API
export const getTimezone = () => {
  const { timezone, useSystemTimezone } = useAnalyticsStore.getState()
  if (useSystemTimezone || !timezone) {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  }
  return timezone
}
